import { useState } from "react";
import { SettingLinks } from "../premiumPaywallCheckout";
import { CustomerDetails, DetailUpdateError } from "../../../../../global/paywall/useDetailsUpdate";
interface PaywallCheckoutPersonalDetailsProps {
    nextStep: () => void
    settingLinks: SettingLinks
    setCustomerDetails: (value: CustomerDetails) => void
    customerDetails: CustomerDetails
    agreeToTermsOfUse: boolean
    setAgreeToTermsOfUse: (value: boolean) => void
    error: DetailUpdateError
    clearError: () => void
    saveCustomerDetails: () => Promise<boolean>
}

const validationSchema = {
    email: {
        required: false,
        message: "שדה חובה",
        validate: (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) || 'כתובת דוא"ל לא תקינה',
    },
    firstName: {
        required: true,
        message: "שדה חובה",
        validate: (value: string) => {
            if (value.length < 2) return "נדרש מינימום תווים";
            if (value.length > 50) return "שם פרטי יכול להכיל עד 50 תווים";
            return /^[A-Za-zא-ת\u0590-\u05FF' ]{2,}$/u.test(value) || "שם פרטי יכול להכיל רק אותיות";
        },
    },
    lastName: {
        required: true,
        message: "שדה חובה",
        validate: (value: string) => {
            if (value.length < 2) return "נדרש מינימום תווים";
            if (value.length > 50) return "שם משפחה יכול להכיל עד 50 תווים";
            return /^[A-Za-zא-ת\u0590-\u05FF' ]{2,}$/u.test(value) || "שם משפחה יכול להכיל רק אותיות";
        },
    },
    phoneNumber: {
        required: true,
        message: "שדה חובה",
        validate: (value: string) => {
            if (isNaN(Number(value))) {
                return "מספר טלפון חייב להכיל ספרות בלבד";
            }
            return /^05\d{8}$/.test(value) || "מספר טלפון לא תקין"
        }
    },
    personalId: {
        required: true,
        message: "שדה חובה",
        validate: (value: string) => {
            if (isNaN(Number(value))) {
                return "מספר תעודת זהות חייב להכיל ספרות בלבד"
            }
            const isValidIsraeliID = (id: string): boolean => {
                if (!/^\d{9}$/.test(id)) return false;
                const idArray = id.split("").map(Number);
                let sum = 0;
                for (let i = 0; i < 9; i++) {
                    let num = idArray[i] * (i % 2 === 0 ? 1 : 2);
                    sum += num > 9 ? num - 9 : num;
                }
                return sum % 10 === 0;
            };

            return isValidIsraeliID(value) || "תעודת זהות לא תקינה";
        }
    },
    termsOfuse: {
        required: true,
        message: "יש לאשר תנאי שימוש להמשך תהליך הרכישה",
        validate: (value: boolean) => value || "יש לאשר תנאי שימוש להמשך תהליך הרכישה",
    }

};


const PaywallCheckoutPersonalDetails = (props: PaywallCheckoutPersonalDetailsProps) => {
    const { nextStep, settingLinks: { terms, privacy, contact }, error, clearError, setCustomerDetails, customerDetails, setAgreeToTermsOfUse, saveCustomerDetails, agreeToTermsOfUse } = props;

    const fieldConfig = {
        email: { id: 1, name: "email", type: "text", label: "", placeholder: '', disabled: true, order: 1 },
        firstName: { id: 2, name: "firstName", type: "text", placeholder: "*שם פרטי", label: '', disabled: false, order: 2 },
        lastName: { id: 3, name: "lastName", type: "text", placeholder: "*שם משפחה", label: '', disabled: false, order: 3 },
        personalId: { id: 6, name: "personalId", type: "tel", inputMode: "numeric", pattern: "[0-9]*", placeholder: "*מספר תעודת זהות", label: '', disabled: false },
        phoneNumber: { id: 5, name: "phoneNumber", type: "tel", inputMode: "numeric", pattern: "[0-9]*", label: "", placeholder: "*טלפון נייד", disabled: false },
        termsOfuse: {
            id: 7, name: "termsOfuse", type: "checkbox", placeholder: '', disabled: false,
            label: `מסכים/ה ל<a href="${terms.url}" target="_blank">תנאי השימוש</a>, <a href="${privacy.url}" target="_blank">מדיניות פרטיות</a>, וקבלת פניות שיווקיות כולל טלפוניות. הודעות תפעוליות ימשיכו להישלח בתקופת המינוי גם לאחר הסרתי מרשימת מדוברים.`
        },
    };

    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const errorMesssagesServer: Partial<Record<DetailUpdateError, string>> = {
        [DetailUpdateError.EXISTING_PERSONAL_ID]: `פרטי תעודת הזהות קיימים במערכת.<br /> לבירור צרו קשר עם <a target="_blank" href=${contact.url}>שירות הלקוחות</a>`,
        [DetailUpdateError.UNKNOWN_ERROR]: "אירעה שגיאה במערכת, אנא נסו שוב מאוחר יותר",
    }

    const validateField = (name, value) => {
        const schema = validationSchema[name];
        if (!schema) return "";
        if (schema.required) {
            if (!value) return schema.message;
            if (typeof value === "boolean" && !value) return schema.message;
            if (typeof value === "string" && !value.trim()) return schema.message;
        }
        if (schema.validate) {
            return typeof schema.validate(value) === "boolean" ? "" : schema.validate(value);
        }
        return "";
    };

    const handleChange = (e) => {
        let { name, value } = e.target;

        if (name === "termsOfuse") {
            setAgreeToTermsOfUse(e.target.checked);
            setErrors({
                ...errors,
                [name]: validateField(name, e.target.checked),
            });
            return;
        }
        if (name === "personalId" && (error === DetailUpdateError.EXISTING_PERSONAL_ID || error === DetailUpdateError.UNKNOWN_ERROR)) {
            clearError();
        }

        if ((name === "personalId" || name === 'phoneNumber') && isNaN(Number(value))) {
            value = value.replace(/\D/g, "");
            setErrors({
                ...errors,
                [name]: validateField(name, "abc"),
            });
            return;
        }

        setCustomerDetails({
            ...customerDetails,
            [name]: value,
        });
        setErrors({
            ...errors,
            [name]: validateField(name, value),
        });
    };



    const handleSubmit = async (e) => {
        e.preventDefault();

        let newErrors = {};
        let isValid = true;

        Object.keys(fieldConfig).forEach((key) => {
            let keyValue = key === 'termsOfuse' ? agreeToTermsOfUse : customerDetails[key];
            const errorMessage = validateField(key, keyValue);
            if (errorMessage) {
                newErrors[key] = errorMessage;
                isValid = false;
            }
        });

        setErrors(newErrors);

        if (!isValid) {
            console.log("Form has errors:", newErrors);
            return;
        }

        const response = await saveCustomerDetails();
        if (response) {
            console.log("Form submitted successfully:", customerDetails);
            nextStep();
        }
    };



    return (
        <div className="PaywallCheckoutPersonalDetails">
            <div className="title">פרטי המנוי/ה</div>
            <div className="personal-details-form">
                <form id="checkout-personal-details" onSubmit={handleSubmit}>
                    {Object.entries(fieldConfig).map(([fieldKey, fieldDef]) => {
                        return (
                            <div className={`input-field ${fieldDef.name}`} key={fieldDef.id}>
                                <div>
                                    <label dangerouslySetInnerHTML={{ __html: fieldDef.label }} />
                                    <input
                                        placeholder={fieldDef.placeholder}
                                        disabled={fieldDef.disabled}
                                        type={fieldDef.type}
                                        name={fieldDef.name}
                                        value={fieldDef.name === 'termsOfuse' ? agreeToTermsOfUse : customerDetails[fieldDef.name]}
                                        onChange={handleChange}
                                        className={errors[fieldKey] || (fieldDef.name === 'personalId' && error === DetailUpdateError.EXISTING_PERSONAL_ID) ? 'error' : ''}
                                    />
                                </div>
                                <div className="input-field-error">
                                    {errors[fieldKey] && <span>{errors[fieldKey]}</span>}
                                    {!errors[fieldKey] && fieldDef.name === 'personalId' && (error === DetailUpdateError.EXISTING_PERSONAL_ID || error === DetailUpdateError.UNKNOWN_ERROR) && (
                                        <span className={ error === DetailUpdateError.EXISTING_PERSONAL_ID ? 'id-exist' : ''} dangerouslySetInnerHTML={{ __html: errorMesssagesServer[error] }} />
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </form>
                <button form="checkout-personal-details" className="submit-button" type="submit">המשך</button>
            </div>
        </div>
    );
};

export default PaywallCheckoutPersonalDetails;